import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Pagination } from "swiper";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";
import { BackgroundColorI, ChipColorI, TitleType } from "../Types";
import { getTitleHTMLTag } from "../../helpers/tools";
import Button from "../Inputs/Button";
import ItemListRepeatable from "../ItemListRepeatable/ItemListRepeatable";
import { useDevice } from "../Hooks";

interface PriceCardsI {
  data: {
    BackgroundColor: BackgroundColorI;
    PriceCardsContent: {
      ChipColor: ChipColorI;
      FirstLabel: string;
      ChipLabel: string;
      FirstTitle: string;
      HasPrimaryButton: boolean;
      ItemsList: {
        Icon: "Red_cross" | "Green_check";
        Label: string;
      };
      PrimaryButtonID: string;
      PrimaryButtonLabel: string;
      PrimaryButtonURL: string;
      PrimaryNewTab: boolean;
      PrimaryNoFollow: boolean;
      HasGhostButton: boolean;
      GhostButtonLabel: string;
      GhostButtonURL: string;
      GhostNoFollow: boolean;
      GhostNewTab: boolean;
      GhostButtonID: string;
      SecondLabel: string;
      SecondTitle: string;
      ThirdLabel: string;
    }[];
    HasSubtitle: boolean;
    HasTitle: boolean;
    SerifTitle: string;
    SansTitle: string;
    Subtitle: string;
    TitleHeading: {
      TitleTypeSelection: TitleType;
    };
  };
}

const PriceCards = ({ data }: PriceCardsI) => {
  const { isMobile } = useDevice();
  const {
    BackgroundColor,
    PriceCardsContent,
    HasSubtitle,
    HasTitle,
    SansTitle,
    SerifTitle,
    Subtitle,
    TitleHeading,
  } = data;

  const backgroundColor = {
    Blue_Gray: "#F5F7FA",
    Light_Blue: "#ECF0FF",
    White: "#FFFFFF",
  }[BackgroundColor.BackgroundColor || "White"];

  const cardBackgroundColor = {
    Blue_Gray: "#FFFFFF",
    Light_Blue: "#FFFFFF",
    White: "#F5F7FA",
  }[BackgroundColor.BackgroundColor || "White"];

  const titleC = HasTitle
    ? getTitleHTMLTag(
        <>
          <span className="font-DM font-bold H2">{SansTitle} </span>
          <span className="font-MW font-light H2">{SerifTitle}</span>
        </>,
        TitleHeading?.TitleTypeSelection,
        "text-[#424242] md:text-center"
      )
    : null;

  const subtitleC = HasSubtitle
    ? getTitleHTMLTag(
        <span className="font-DM font-medium H4">{Subtitle}</span>,
        "p",
        "text-[#424242] md:text-center"
      )
    : null;

  const cards = PriceCardsContent && PriceCardsContent.length > 0
    ? [...PriceCardsContent].map((content, idx) => {
        const isHighlighted = idx === 1;
        return (
          <CardContent
            data={content}
            backgroundColor={cardBackgroundColor}
            isHighlighted={isHighlighted}
            key={`${content.FirstLabel}_${idx}`}
          />
        );
      })
    : null;

  return (
    <section className="overflow-hidden" style={{ backgroundColor }}>
      <div className="flex flex-col max-w-content mx-auto py-[64px] xl:px-[0px] px-[24px] md:gap-[64px] gap-[32px]">
        <div className="flex flex-col md:gap-[16px] gap-[8px]">
          {titleC}
          {subtitleC}
        </div>
        {cards ? (
          isMobile ? (
            <>
              <Swiper
                slidesPerView={1.15}
                spaceBetween={16}
                freeMode={true}
                pagination={{ clickable: true, el: ".swiper-price-pagination" }}
                modules={[FreeMode, Pagination]}
                className="!w-full"
              >
                {[...(PriceCardsContent || [])].map((content, idx) => (
                  <SwiperSlide key={`${content.FirstLabel}_${idx}`}>
                    <CardContent
                      data={content}
                      backgroundColor={cardBackgroundColor}
                      isHighlighted={idx === 1}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className="swiper-price-pagination flex justify-center gap-[5px] mt-[16px]" />
            </>
          ) : (
            <div className="grid lg:grid-cols-3 gap-[32px] justify-center">
              {cards}
            </div>
          )
        ) : null}
      </div>
    </section>
  );
};

const CardContent = ({ data, backgroundColor, isHighlighted }: any) => {
  const {
    ChipColor,
    FirstLabel,
    ChipLabel,
    FirstTitle,
    HasPrimaryButton,
    ItemsList,
    PrimaryButtonID,
    PrimaryButtonLabel,
    PrimaryButtonURL,
    PrimaryNewTab,
    PrimaryNoFollow,
    HasGhostButton,
    GhostButtonLabel,
    GhostButtonURL,
    GhostNoFollow,
    GhostNewTab,
    GhostButtonID,
    SecondLabel,
    SecondTitle,
    ThirdLabel,
  } = data;

  // The three cards repeat the same visible CTA label ("Calcular mi precio")
  // with different destinations, which Lighthouse flags as identical link
  // names. Prefix the card title so each link gets a unique accessible name
  // while still starting with the visible label (WCAG 2.5.3 Label in Name).
  const ctaAriaLabel = (label: string) =>
    label && FirstTitle ? `${label}: ${FirstTitle}` : undefined;

  const isChipEnabled = ChipColor?.ChipColor !== "No_chip";
  const chipStyles = {
    Green: {
      backgroundColor: "#E6F3F0",
      color: "#016B51",
      border: "1px solid transparent",
    },
    White: {
      backgroundColor: "#FFFFFF",
      color: "#3430E3",
      border: "1px solid #423EF4",
    },
  }[ChipColor?.ChipColor || "Green"];

  return (
    <div
      className={`rounded-[24px] flex-[352px] max-w-[352px] overflow-hidden p-[24px] flex flex-col lg:row-auto ${
        isHighlighted ? "row-start-1" : ""
      }`}
      style={{
        backgroundColor,
        boxShadow: isHighlighted
          ? "0px 24px 38px 0px rgba(0, 0, 0, 0.07), 0px 9px 46px 0px rgba(0, 0, 0, 0.06), 0px 11px 15px 0px rgba(0, 0, 0, 0.10)"
          : "0px 1px 1px 0px rgba(0, 0, 0, 0.07), 0px 2px 1px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)",
        border: isHighlighted ? "2px solid #8E8BF8" : "",
      }}
    >
      <div className="flex flex-row gap-[8px] mb-[20px]">
        <p className="flex flex-col flex-1">
          <span className="BodyM font-normal text-[#686868] tracking-[1.2px]">
            {FirstLabel}
          </span>
          <span className="H4 font-bold text-[#423EF4]">{FirstTitle}</span>
        </p>
        {isChipEnabled ? (
          <p
            className="BodyS font-normal py-[4px] px-[8px] rounded-full h-fit"
            style={{ ...chipStyles }}
          >
            {ChipLabel}
          </p>
        ) : null}
      </div>
      <div className="flex flex-col gap-[16px]">
        <div className="flex flex-col">
          <span className="BodyM font-normal text-[#686868] tracking-[1.2px]">
            {SecondLabel}
          </span>
          <p>
            <span className="H1 font-bold text-[#424242]">{SecondTitle}</span>{" "}
            <span className="BodyS text-[#686868]">{ThirdLabel}</span>
          </p>
        </div>
        <div className="h-[1px] w-full bg-[#E2E2E2]" />
        <ItemListRepeatable items={ItemsList} />
      </div>
      {HasPrimaryButton || HasGhostButton ? (
        <div className="mt-auto pt-[32px] space-y-[8px]">
          {HasPrimaryButton ? (
            <Button
              label={PrimaryButtonLabel}
              href={PrimaryButtonURL}
              ghost={false}
              newTab={PrimaryNewTab}
              noFollow={PrimaryNoFollow}
              id={PrimaryButtonID}
              ariaLabel={ctaAriaLabel(PrimaryButtonLabel)}
              size="40"
              className="!w-fit mx-auto text-center"
            />
          ) : null}
          {HasGhostButton ? (
              <Button
                label={GhostButtonLabel}
                href={GhostButtonURL}
                ghost={true}
                secondary={true}
                newTab={GhostNewTab}
                noFollow={GhostNoFollow}
                id={GhostButtonID}
                ariaLabel={ctaAriaLabel(GhostButtonLabel)}
                size="40"
                className="!w-fit mx-auto text-center"
              />
            ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default PriceCards;
